import { useCallback, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FileRejection, useDropzone } from "react-dropzone";

//** COMPONENT */
import { Box, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ModalCustom from "../../../component/modalCustom/modalCustom";
import InputTextField from "../../../component/input/inputTextField";
import ButtonCustom from "../../../component/button/buttonCustom";
import Loading from "../../../component/loading/loading";
import { notiError, notiSuccess } from "../../../component/notification/notifications";
import Cloudupload from "../../../assets/icon/cloudupload";

//** API */
import RepairApi from "../../../api/repair/repair.api";
import FileuploadApi from "../../../api/uploadfile/upload.api";

//** UTILS */
import { colors } from "../../../constants/colors";
import { REPAIR_ADDITIONAL_FILE_TYPE, repairStatusLabel } from "../../../constants/repair";
import { dateToView } from "../../../utils/date.utils";
import { getApiErrorMessage } from "../../../utils/api-error.utils";
import { formatFileSize } from "../../../utils/common";

//** STYLE */
import { Boxfilearray } from "../add/addRepairStyle";
import { stallLabelOf } from "../../../utils/stall.utils";

type Props = {
  repairId: number;
  repairNo?: string;
  /** วันที่แจ้ง — ใช้ createDate ก่อน เพราะ repairDate ถูก PATCH ทับด้วยวันนัดตอนรับคำร้อง */
  createDate?: string;
  repairDate?: string;
  status?: string;
  zoneName?: string;
  stallName?: string;
  stallNumber?: string;
  /** "TRUE" | "FALSE" — backend รับเป็น string ไม่ใช่ boolean */
  centralCheck?: string;
  centralDescription?: string | null;
  /** เรื่องเดิม = description ของใบหลัก */
  description?: string;
  onClose: () => void;
  /** สำเร็จแล้ว: parent ปิด modal + reload ตาราง ในจังหวะเดียว modal จะไม่เรียก onClose ซ้ำ */
  onSuccess: () => void;
};

const MAX_FILES = 10;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_DESCRIPTION_LENGTH = 500;
/** react-dropzone 14 ต้องใช้ accept แบบ object — และให้ accept เป็นตัวคัดกรองเดียว ไม่เช็คนามสกุลซ้ำใน onDrop */
const ACCEPT_IMAGE = { "image/jpeg": [".jpg", ".jpeg"], "image/png": [".png"] };

export default function ModalRepairAdditional({
  repairId,
  repairNo,
  createDate,
  repairDate,
  status,
  zoneName,
  stallName,
  stallNumber,
  centralCheck,
  centralDescription,
  description,
  onClose,
  onSuccess,
}: Props) {
  const [additionalText, setAdditionalText] = useState<string>("");
  const [errorAdditionalText, setErrorAdditionalText] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  /** กันกดปุ่มรัวก่อน isSubmitting จะ re-render ทัน */
  const isSubmittingRef = useRef<boolean>(false);

  const onDropRejected = useCallback((rejections: FileRejection[]) => {
    const isTooBig = rejections.some((rejection) =>
      rejection.errors.some((error) => error.code === "file-too-large")
    );
    notiError(
      isTooBig
        ? `ไฟล์มีขนาดเกิน ${formatFileSize(MAX_FILE_SIZE)}`
        : "รับเฉพาะไฟล์รูปภาพ JPG หรือ PNG"
    );
  }, []);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles?.length) return;

      const nextFiles = [...files];
      let duplicated = 0;
      let overflow = 0;

      acceptedFiles.forEach((acceptedFile) => {
        if (nextFiles.length >= MAX_FILES) {
          overflow += 1;
          return;
        }
        // เทียบ name + size + lastModified เพราะ path ไม่ได้มีทุก browser
        const isDuplicated = nextFiles.some(
          (x) =>
            x.name === acceptedFile.name &&
            x.size === acceptedFile.size &&
            x.lastModified === acceptedFile.lastModified
        );
        if (isDuplicated) {
          duplicated += 1;
          return;
        }
        nextFiles.push(acceptedFile);
      });

      if (duplicated) notiError("ไฟล์นี้ถูกแนบไว้แล้ว");
      if (overflow) notiError(`แนบไฟล์ได้ไม่เกิน ${MAX_FILES} ไฟล์`);
      setFiles(nextFiles);
    },
    [files]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: ACCEPT_IMAGE,
    multiple: true,
    maxSize: MAX_FILE_SIZE,
    disabled: isSubmitting,
  });

  /** ลบด้วย index ไม่ใช่ path — path เป็น undefined ได้ทุกไฟล์ ซึ่งจะทำให้ filter ลบยกลิสต์ */
  const handleRemoveFile = (index: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== index));

  const onSubmit = async () => {
    const text = additionalText.trim();
    if (!text) {
      setErrorAdditionalText("ระบุเรื่องที่แจ้งเพิ่ม");
      return;
    }
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      // ฟอร์มเก็บช่องเดียว จึงส่งแค่ description ไม่ส่ง title
      const res = await RepairApi.additionalCreate({ repairId, description: text });
      const refId = res?.data?.refId;

      if (files.length > 0) {
        if (!refId) {
          notiError("แจ้งเรื่องเพิ่มเติมสำเร็จ แต่ไม่ได้รับรหัสอ้างอิงสำหรับแนบไฟล์");
          onSuccess();
          return;
        }
        const uploadFile = new FormData();
        uploadFile.append("refId", `${refId}`);
        uploadFile.append("fileType", REPAIR_ADDITIONAL_FILE_TYPE);
        files.forEach((file) => uploadFile.append(`files[]`, file));
        try {
          // ไม่ set Content-Type เอง axios ต้องลบ header ให้ browser ใส่ boundary ของ multipart
          await FileuploadApi.uploadfilearray(uploadFile);
        } catch (uploadError) {
          // record ถูกสร้างแล้ว ห้ามบอกว่า "ไม่สำเร็จ" ผู้ใช้จะกดซ้ำแล้วได้ record ซ้ำ
          notiError(
            getApiErrorMessage(
              uploadError,
              "แจ้งเรื่องเพิ่มเติมสำเร็จ แต่แนบไฟล์ไม่สำเร็จ กรุณาแนบไฟล์ใหม่อีกครั้ง"
            )
          );
          onSuccess();
          return;
        }
      }

      notiSuccess("แจ้งเรื่องเพิ่มเติมสำเร็จ");
      onSuccess();
    } catch (error) {
      notiError(getApiErrorMessage(error, "แจ้งเรื่องเพิ่มเติมไม่สำเร็จ"));
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  /** repairDate ถูกทับด้วยวันนัดตอนรับคำร้อง จึงเอา createDate มาก่อน */
  const reportedAt = createDate || repairDate;
  const subTitle = [
    repairNo && `เลขที่เอกสาร ${repairNo}`,
    // dateToView(undefined) format วันนี้เงียบๆ ต้อง guard ก่อนเสมอ
    reportedAt && `แจ้งเมื่อ ${dateToView(reportedAt)}`,
    status && repairStatusLabel(status),
  ]
    .filter(Boolean)
    .join(" · ");

  const stallText =
    centralCheck === "TRUE"
      ? centralDescription || "ส่วนกลาง"
      : [stallLabelOf(stallNumber, stallName, ""), zoneName && `(โซน ${zoneName})`]
          .filter(Boolean)
          .join(" ");

  const renderLabel = (label: string, required?: boolean) => (
    <Col md={3} xs={12} className="my-2">
      <Typography sx={{ fontSize: 16, color: colors.textLightGray }}>
        {label} {required && <span style={{ color: colors.red }}>*</span>} :
      </Typography>
    </Col>
  );

  const renderReadOnlyValue = (value?: string | null) => (
    <Col md={9} xs={12} className="my-2">
      <Typography sx={{ fontSize: 16, whiteSpace: "pre-line", wordBreak: "break-word" }}>
        {value || "-"}
      </Typography>
    </Col>
  );

  const renderSelectedFiles = () => (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: 14, color: colors.textLightGray, mb: 1 }}>
        {`${files.length}/${MAX_FILES} ไฟล์`}
      </Typography>
      {files.map((file, index) => (
        <Boxfilearray key={`${file.name}-${file.lastModified}-${index}`} sx={{ mt: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
            <Typography sx={{ fontSize: 16, wordBreak: "break-all" }}>{file.name}</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                sx={{ fontSize: 14, color: colors.textLightGray, whiteSpace: "nowrap" }}
              >
                {formatFileSize(file.size)}
              </Typography>
              <DeleteIcon
                color="error"
                sx={{ cursor: isSubmitting ? "not-allowed" : "pointer" }}
                onClick={() => {
                  if (!isSubmitting) handleRemoveFile(index);
                }}
              />
            </Box>
          </Box>
        </Boxfilearray>
      ))}
    </Box>
  );

  const renderBody = () => (
    <Box
      className="custom-scroll"
      sx={{
        px: { xs: 2, md: 3 },
        pb: 1,
        maxHeight: "calc(100vh - 260px)",
        overflowY: "auto",
      }}
    >
      {subTitle && (
        <Typography
          sx={{ fontSize: 16, color: colors.textLightGray, textAlign: "center", mb: 2 }}
        >
          {subTitle}
        </Typography>
      )}

      <Row>
        {renderLabel("โซน / แผงค้า")}
        {renderReadOnlyValue(stallText)}

        {renderLabel("เรื่องเดิม")}
        {renderReadOnlyValue(description)}

        {renderLabel("เรื่องที่แจ้งเพิ่ม", true)}
        <Col md={9} xs={12} className="my-2">
          <InputTextField
            value={additionalText}
            placeholder="ระบุอาการหรือสิ่งที่พบเพิ่มเติม"
            helperText={errorAdditionalText}
            disabled={isSubmitting}
            inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
            onchange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setAdditionalText(e.target.value);
              if (errorAdditionalText) setErrorAdditionalText("");
            }}
          />
        </Col>

        {renderLabel("แนบไฟล์")}
        <Col md={9} xs={12} className="my-2">
          <Box
            {...getRootProps()}
            sx={{
              width: "100%",
              border: `2px dashed ${colors.bgText}`,
              borderRadius: "8px",
              py: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              backgroundColor: isDragActive ? colors.extraLightGray : colors.white,
            }}
          >
            <input {...getInputProps()} />
            <Cloudupload />
            <Typography sx={{ mt: 1, fontSize: 16, fontFamily: "kanit" }}>
              แนบไฟล์รูปภาพ JPG, PNG
            </Typography>
          </Box>
          {files.length > 0 && renderSelectedFiles()}
        </Col>
      </Row>

      {/* ปุ่มอยู่ใน body ไม่ใช่ footer — BtnConfirm ของ ModalCustom เป็น width: max-content ใน Col md="auto" ทำเต็มความกว้างไม่ได้ */}
      <ButtonCustom
        onClick={onSubmit}
        disabled={!additionalText.trim() || isSubmitting}
        textButton="ส่งเรื่องเพิ่มเติม"
        btnStyle={{ fontSize: "16px", width: "100%", padding: "0px", marginTop: "24px" }}
      />
    </Box>
  );

  return (
    <>
      {isSubmitting && <Loading show />}
      <ModalCustom
        // ต้องเป็น true เสมอ + ให้ parent conditional render (ค่า false จะ fall back ไปอ่าน redux)
        isShowProps
        // ปิด modal เองผ่าน onClose ไม่ให้ไป reset redux modal.isShow ของ modal อื่นในหน้าเดียวกัน
        autoResetModal={false}
        size="lg"
        closeButton
        alignFooter="center"
        title="แจ้งเรื่องเพิ่มเติม"
        component={renderBody()}
        // ไม่ส่ง onSubmit/textBtnConfirm/textBtnCancel footer จึงไม่มีปุ่ม เหลือแค่ X มุมขวาบนตามแบบ
        onClose={onClose}
      />
    </>
  );
}
